import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Box,
  Button,
  Form,
  ModalContext,
  SectionDivider,
  SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import ToggleListItem from '@components/ToggleListItem'

import useWebhook, { useWebhookForm } from '@hooks/useWebhook'

import { providerWebhookEvents, systemWebhookEvents } from '@functions/webhook'

const confirmDelete = (deleteWebhook, deleteFn, closeModal) => {
  const deleteWebhookPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Webhook Deleted',
  }

  swal.fire({
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, delete it.',
    icon: 'warning',
    showCancelButton: true,
    text: 'Are you sure?',
    title: 'Delete Webhook?',
  }).then(({ value }) => {
    if (value){
      deleteWebhook(deleteWebhookPayload)
    }
  })
}

const createOrUpdateWebhook = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createWebhook, updateWebhook },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const webhookPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Webhook ${text}`,
  }

  if (entityState.id) return updateWebhook(webhookPayload)

  return createWebhook(webhookPayload)
}

const updateEnabledEvent = (params) => {
  const {
    eventEnabled,
    eventKey,
    entityState: { enabled_events },
    setEntityState,
  } = params

  const updatedEnabledEvents = {
    ...enabled_events,
    [eventKey]: !eventEnabled,
  }

  setEntityState({ enabled_events: updatedEnabledEvents })
}

const CreateOrEditWebhookModal = (props) => {
  const {
    callbacks, modalKey, showModal,
  } = props
  const { closeModal, deleteWebhook } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { webhook } = modalPayload

  const {
    callbacks: {
      createWebhook: createFn,
      deleteWebhook: deleteFn,
      updateWebhook: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useWebhook(webhook)

  const {
    entityState,
    entityState: {
      active,
      enabled_events: enabledEvents,
      enable_all_events,
      id,
      log_email,
      owner_type,
      secret_key,
      title,
      url,
      webhook_version,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useWebhookForm(webhook, { validateOn: 'change' })

  const events = owner_type === 'System' ? systemWebhookEvents : providerWebhookEvents

  const saveOrCreateButtonText = id ? 'Save Changes' : 'Create Webhook'

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit' : 'Create'}
        titleSecondLine="Webhook"
      />

      <SidebarModal.Content>
        <Box flexDirection="column" flexShrink={0}>
          <Box>
            <Form.Field label="* Version" boxProps={{ width: '30%', marginRight: 'small' }}>
              <select name="webhook_version" value={webhook_version} {...handlers}>
                <option value="V1">V1</option>
              </select>
            </Form.Field>

            <Form.Field
              boxProps={{ width: 'calc(70% - 4px)' }}
              errorMessage={errors.title}
              label="* Title"
            >
              <input
                name="title"
                type="text"
                value={title}
                {...handlers}
              />
            </Form.Field>
          </Box>

          <Form.Field
            label="* Webhook URL"
            errorMessage={errors.url}
            marginTop="large"
          >
            <input
              name="url"
              type="text"
              value={url}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="* Secret Key" marginTop="large">
            <input
              name="secret_key"
              type="text"
              value={secret_key}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Log Email" marginTop="large">
            <input
              name="log_email"
              type="text"
              value={log_email}
              {...handlers}
            />
          </Form.Field>

          <SectionDivider marginTop="large">WEBHOOK SETTINGS</SectionDivider>

          <ToggleListItem
            onClick={() => setEntityState({ active: !active })}
            toggleOnText="Yes"
            toggleOffText="No"
            toggleValue={active}
          >
            <ToggleListItem.Title>Webhook is Active</ToggleListItem.Title>
            <ToggleListItem.Description>
              This webhook is active and running whenever required by its settings.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            onClick={() => setEntityState({ enable_all_events: !enable_all_events })}
            toggleOnText="Yes"
            toggleOffText="No"
            toggleValue={enable_all_events}
          >
            <ToggleListItem.Title>All Events</ToggleListItem.Title>
            <ToggleListItem.Description>
              This webhook is active and running whenever required by its settings.
            </ToggleListItem.Description>
          </ToggleListItem>

          {!enable_all_events && (
            <>
              <SectionDivider boxProps={{ marginBottom: 'medium' }}>EVENTS</SectionDivider>

              {events.map((event) => {
                const eventEnabled = enabledEvents[event.key] || false

                return (
                  <ToggleListItem
                    boxProps={{ borderBottom: '1px dashed', borderColor: 'lineColor', paddingBottom: 'large' }}
                    key={event.key}
                    onClick={() => updateEnabledEvent({
                      eventKey: event.key, eventEnabled, entityState, setEntityState,
                    })}
                    toggleOnText="Yes"
                    toggleOffText="No"
                    toggleValue={eventEnabled}
                  >
                    <ToggleListItem.Title>{event.label}</ToggleListItem.Title>
                  </ToggleListItem>
                )
              })}
            </>
          )}

          {id && (
            <Form.Field direction="column" label="More Options" boxProps={{ marginTop: 'medium' }}>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(deleteWebhook, deleteFn, closeModal)}
                size="medium"
              >
                Delete Webhook
              </Button>
            </Form.Field>
          )}
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateWebhook(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditWebhookModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditWebhookModal.defaultProps = {
  modalKey: 'CreateOrEditWebhookModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditWebhookModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
